// import differenceInYears from "date-fns/differenceInYears";
import { differenceInYears, isDate, parse } from 'date-fns';
import * as yup from 'yup';
import 'yup-phone-lite';

import { buildQuoteSummarySchema, buildSchemaSelectCover } from './selectCoverSchema.js';
import {
  LIFE_COVER_MAX_AGE,
  LIFE_COVER_MIN_AGE,
  ADDITIONAL_HOLDER_MIN_AGE,
  ADDITIONAL_HOLDER_MAX_AGE,
} from './sharedConstValues.js';

//* ----------------------------------------------------------------------
//** Your Details Personal */
//* Question options
// const OPTION_SELECT = 'Please select one option';

//* Title
export const TITLE = 'Title is required';
//* First Name
export const MAX_NUMBER_CHARACTERS = 20;
export const FIRST_NAME_REQUIRED = 'You must enter a first name';
export const FIRST_NAME_LENGTH = `First name must be less than ${MAX_NUMBER_CHARACTERS} characters`;
// 1) Regex that forbids a leading space and digits everywhere
export const FIRST_NAME_REGEX = /^[A-Za-z'`-][A-Za-z'`\-\s]*$/;
export const FIRST_NAME_NOT_VALID = 'Please enter a valid first name';
export const FIRST_NAME_NO_LEADING_SPACES = 'First name cannot start with a space';
export const FIRST_NAME_NO_FULL_NAME_SPACES = 'First name cannot be just spaces';
//* Surname
export const SURNAME_REQUIRED = 'You must enter a surname';
export const SURNAME_LENGTH = `Surname must be less than ${MAX_NUMBER_CHARACTERS} characters`;
// 1) Regex that forbids a leading space and digits everywhere
export const SURNAME_REGEX = /^[A-Za-z'`-][A-Za-z'`\-\s]*$/;
export const SURNAME_NOT_VALID = 'Please enter a valid surname';
export const SURNAME_NO_LEADING_SPACES = 'Surname cannot start with a space';
export const SURNAME_NO_FULL_NAME_SPACES = 'Surname cannot be just spaces';
//* Gender
const GENDER = 'You must select a gender';
//* DOB
export let DOB_MIN_AGE = `You must be at least ${LIFE_COVER_MIN_AGE.toString()} to apply for cover`;
export let DOB_MAX_AGE = `The maximum age at which you can apply for Life cover is
  ${LIFE_COVER_MAX_AGE.toString()}`;
//* Tobacco
const TOBACCO_QUESTION_REQUIRED = 'Tobacco question is required';

//* ADDITIONAL DOB
export let ADDITIONAL_DOB_MIN_AGE = `An Additional Policy Holder must be at least ${ADDITIONAL_HOLDER_MIN_AGE.toString()}.`;
export let ADDITIONAL_DOB_MAX_AGE = `The maximum age of an Additional Policy Holder is 
  ${ADDITIONAL_HOLDER_MAX_AGE.toString()}.`;

export const DOB_REQUIRED = 'You must enter a date of birth';
export const INVALID_DATE = 'Invalid date';

export function parseDateString(value, originalValue) {
  const parsedDate = isDate(originalValue)
    ? originalValue
    : parse(originalValue, 'dd/MM/yyyy', new Date());

  return parsedDate;
}

//** ------------------------------------------------ */
const buildGettingStartedCCSchema = (person) => {
  return {
    [`${person}_firstName`]: yup
      .string()
      .trim()
      .required(FIRST_NAME_REQUIRED)
      .max(MAX_NUMBER_CHARACTERS, FIRST_NAME_LENGTH)
      .matches(FIRST_NAME_REGEX, {
        message: FIRST_NAME_NOT_VALID,
        excludeEmptyString: true, // skip regex on ""
      })
      // 2) Explicitly reject inputs that *originally* started with a space
      .test('no-leading-space', FIRST_NAME_NO_LEADING_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // still optional
        return !/^\s/.test(originalValue);
      })
      // 3) If you ALSO want to block "all spaces" (while still allowing empty after clear):
      .test('not-only-spaces', FIRST_NAME_NO_FULL_NAME_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // allow blank
        return originalValue.trim().length > 0; // reject "   "
      }),
    [`${person}_surname`]: yup
      .string()
      .trim() // trims before validation
      .required(SURNAME_REQUIRED)
      .max(MAX_NUMBER_CHARACTERS, SURNAME_LENGTH)
      .matches(FIRST_NAME_REGEX, {
        message: SURNAME_NOT_VALID,
        excludeEmptyString: true, // skip regex on ""
      })
      // 2) Explicitly reject inputs that *originally* started with a space
      .test('no-leading-space', SURNAME_NO_LEADING_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // still optional
        return !/^\s/.test(originalValue);
      })
      // 3) If you ALSO want to block "all spaces" (while still allowing empty after clear):
      .test('not-only-spaces', SURNAME_NO_FULL_NAME_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // allow blank
        return originalValue.trim().length > 0; // reject "   "
      }),
  };
};

const buildGettingStartedOnlineSchema = (person) => {
  return {
    [`${person}_firstName`]: yup
      .string()
      .trim() // trims before validation
      // .nullable()
      // .notRequired()
      .required(FIRST_NAME_REQUIRED)
      .max(MAX_NUMBER_CHARACTERS, FIRST_NAME_LENGTH)
      .matches(FIRST_NAME_REGEX, {
        message: FIRST_NAME_NOT_VALID,
        excludeEmptyString: true, // skip regex on ""
      })
      // 2) Explicitly reject inputs that *originally* started with a space
      .test('no-leading-space', FIRST_NAME_NO_LEADING_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // still optional
        return !/^\s/.test(originalValue);
      })
      // 3) If you ALSO want to block "all spaces" (while still allowing empty after clear):
      .test('not-only-spaces', FIRST_NAME_NO_FULL_NAME_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // allow blank
        return originalValue.trim().length > 0; // reject "   "
      }),
  };
};

const buildGettingStartedDetailsSchema = (person) => {
  return {
    [`${person}_gender`]: yup.string().required(GENDER).oneOf(['male', 'female']),
    [`${person}_dob`]: yup
      .date()
      .nullable()
      .default(undefined)
      .transform(parseDateString)
      .typeError(INVALID_DATE)
      .required(DOB_REQUIRED)
      .test('dob_max', DOB_MAX_AGE, function (value) {
        return differenceInYears(new Date(), new Date(value)) <= LIFE_COVER_MAX_AGE;
      })
      .test('dob_min', DOB_MIN_AGE, function (value) {
        return differenceInYears(new Date(), new Date(value)) >= LIFE_COVER_MIN_AGE;
      }),
    [`${person}_tobaccoQuestion`]: yup
      .string()
      .oneOf(['yes', 'no'])
      .required(TOBACCO_QUESTION_REQUIRED),
  };
};

// function startsOrEndsWithWhitespace(str) {
//   return /^\s|\s$/.test(str);
// }
// const MAILING_ADDRESS_REQUIRED = 'Please ensure a valid address is selected';

// const buildPaymentPolicyMailingAddressDetails = (addressType = 'policyMailingAddress') => {
//   return {
//     [`${addressType}Full`]: yup.mixed().when('$isPolicyMailingAddressAutoComplete', {
//       is: true,
//       then: yup
//         .mixed()
//         .strict()
//         .test('is-policy-mailing-address-object', MAILING_ADDRESS_REQUIRED, function (value) {
//           if (
//             (typeof value === 'object' ||
//               (typeof value === 'string' && startsOrEndsWithWhitespace(value) === false)) &&
//             value
//           ) {
//             return true;
//           }
//           return this.createError({
//             path: `${addressType}Full`,
//             message: MAILING_ADDRESS_REQUIRED,
//           });
//         }),
//       otherwise: yup.mixed().notRequired(),
//     }),
//   };
// };

// ** Two person schema
export function schemaTwoPersonDetailsGettingStarted() {
  return yup
    .object()
    .shape({
      ...buildGettingStartedDetailsSchema('personOne'),
      ...buildGettingStartedDetailsSchema('personTwo'),
    })
    .required();
}

// ** Getting started schema - Online
export function schemaGettingStarted() {
  return yup
    .object()
    .shape({
      ...buildGettingStartedOnlineSchema('personOne'),
      ...buildGettingStartedDetailsSchema('personOne'),
      // eslint-disable-next-line no-use-before-define
      ...buildSaveAndEmailSchema('personOne'),
      // ...buildPaymentPolicyMailingAddressDetails(),
    })
    .required();
}

// ** Getting started schema - CC
export function schemaGettingStartedCC() {
  return yup
    .object()
    .shape({
      ...buildGettingStartedCCSchema('personOne'),
      ...buildGettingStartedDetailsSchema('personOne'),
      ...buildSaveAndEmailSchema('personOne'),
    })
    .required();
}

// ----------------------------------------------------------------------
//** Your Details Contact */

//* Email
const EMAIL_REQUIRED = 'Email is required';
const EMAIL_REGEX =
  /^[A-Za-z0-9!#$%&*+\-/=?^_{|}]+(\.[A-Za-z0-9!#$%&*+\-/=?^_{|}]+)*@[A-Za-z0-9]+(-[A-Za-z0-9]+)*(\.[A-Za-z0-9]+(-[A-Za-z0-9]+)*)*\.([A-Za-z]{2,}|[A-Za-z]+(-[A-Za-z]+)+)$/;
const EMAIL_NOT_VALID = 'Enter a valid email address';
const EMAIL_MAX_LENGTH = 40;
const EMAIL_MAX_LENGTH_MESSAGE = `Email address can only be up to ${EMAIL_MAX_LENGTH} characters`;
const EMAIL_TRIM = 'Email address cannot contain spaces';

//* Contact Number
export const CONTACT_NUMBER_REQUIRED = 'Mobile number is required';
export const CONTACT_NUMBER_NOT_VALID = 'Enter a valid mobile number';
export const CONTACT_NUMBER_STARTING_WITH = 'Mobile number must start with 04';
export const CONTACT_NUMBER_TRIM = 'Mobile number cannot contain spaces';

//* State
const STATE_REQUIRED = 'State is required';

//* Promo Code.
const PROMO_CODE_MAX_LENGTH = 15;
const PROMO_CODE_MAX_LENGTH_MESSAGE = `Promotional code can only be up to ${PROMO_CODE_MAX_LENGTH} characters`;
const PROMO_CODE_ALPHA_NUMERIC = 'Only allowed alphanumeric characters are allowed';
const PROMO_CODE_REGEX = /^[a-zA-Z0-9]*$/;

//* Questions
// const TOBACCO_QUESTION_REQUIRED = 'Tobacco question is required';
const INSURANCE_QUESTION_REQUIRED = 'Insurance question is required';
const INSURANCE_QUESTION_LIST_REQUIRED = 'You must select at least one.';
// const CONSENT_QUESTION_REQUIRED = 'Consent question is required.';

const buildQuestions = (person) => {
  return {
    [`${person}_tobaccoQuestion`]: yup
      .string()
      .oneOf(['yes', 'no'])
      .required(TOBACCO_QUESTION_REQUIRED),
    [`${person}_otherAllianzInsuranceQuestion`]: yup
      .string()
      .oneOf(['yes', 'no'])
      .required(INSURANCE_QUESTION_REQUIRED),
    [`${person}_otherAllianzInsuranceList`]: yup
      .array()
      .when([`${person}_otherAllianzInsuranceQuestion`], {
        is: 'yes',
        then: yup.array().nullable().required(INSURANCE_QUESTION_LIST_REQUIRED),
        otherwise: yup.array().nullable().notRequired(),
      }),
    // [`${person}_callbackConsent`]: yup
    // 	.string()
    // 	.when("$isConsentCheckRequired", {
    // 		is: false,
    // 		then: yup
    // 			.string()
    // 			.oneOf(["yes", "no"])
    // 			.required(CONSENT_QUESTION_REQUIRED),
    // 		otherwise: yup.string().nullable().notRequired(),
    // 	}),
  };
};

const buildContactDetailsSchema = (person) => {
  return {
    [`${person}_emailAddress`]: yup
      .string()
      .strict(true)
      .trim(EMAIL_TRIM)
      .required(EMAIL_REQUIRED)
      .matches(EMAIL_REGEX, EMAIL_NOT_VALID)
      .max(EMAIL_MAX_LENGTH, EMAIL_MAX_LENGTH_MESSAGE),
    [`${person}_contactNumber`]: yup
      .string()
      .strict(true)
      .trim(CONTACT_NUMBER_TRIM)
      .phone('AU', CONTACT_NUMBER_NOT_VALID)
      .required(CONTACT_NUMBER_REQUIRED)
      .test('mobile_number', CONTACT_NUMBER_STARTING_WITH, function (value) {
        return value?.slice(0, 2) === '04';
      }),
    [`${person}_state`]: yup
      .string()
      .oneOf(['act', 'nsw', 'vic', 'qld', 'nt', 'sa', 'tas', 'wa'])
      .required(STATE_REQUIRED),
    [`${person}_promoCode`]: yup
      .string()
      .max(PROMO_CODE_MAX_LENGTH, PROMO_CODE_MAX_LENGTH_MESSAGE)
      .test('is-alphanumeric', PROMO_CODE_ALPHA_NUMERIC, (value) => {
        // Apply validation only if there is a value
        if (value) {
          return PROMO_CODE_REGEX.test(value);
        }
        return true; // If no value, validation passes
      })
      .notRequired(),
    ...buildQuestions('personOne'),
  };
};

// ** Two person schema
export function schemaYourTwoPersonDetailsContact() {
  return yup
    .object()
    .shape({
      ...buildContactDetailsSchema('personOne'),
      ...buildQuestions('personTwo'),
    })
    .required();
}

// ** One person schema
export function schemaYourDetailsContact() {
  return yup
    .object()
    .shape({
      ...buildContactDetailsSchema('personOne'),
    })
    .required();
}

//----------------------------------------------------------------

export const buildSaveAndEmailSchema = (person) => {
  return {
    [`${person}_emailAddress`]: yup
      .string()
      .strict(true)
      .trim(EMAIL_TRIM)
      .required(EMAIL_REQUIRED)
      .matches(EMAIL_REGEX, EMAIL_NOT_VALID)
      .max(EMAIL_MAX_LENGTH, EMAIL_MAX_LENGTH_MESSAGE),
    [`${person}_contactNumber`]: yup
      .string()
      .strict(true)
      .trim(CONTACT_NUMBER_TRIM)
      .phone('AU', CONTACT_NUMBER_NOT_VALID)
      .required(CONTACT_NUMBER_REQUIRED)
      .test('mobile_number', CONTACT_NUMBER_STARTING_WITH, function (value) {
        return value?.slice(0, 2) === '04';
      }),
  };
};

const buildCCMarketingQuestions = () => {
  return {
    optOutDirectMarketingQuestion: yup.string().oneOf(['yes', 'no']).required(),
  };
};

export function schemaSaveAndEmail() {
  return yup
    .object()
    .shape({
      ...buildSaveAndEmailSchema('personOne'),
    })
    .required();
}

export function schemaSaveAndEmailCC() {
  return yup
    .object()
    .shape({
      ...buildSaveAndEmailSchema('personOne'),
      ...buildCCMarketingQuestions(),
    })
    .required();
}
//----------------------------------------------------------------

export function schemaTwoPersonSelectCover() {
  return yup
    .object()
    .shape({
      ...buildSchemaSelectCover('personOne'),
      ...buildSchemaSelectCover('personTwo'),
    })
    .required();
}

export function schemaSelectCover() {
  return yup
    .object()
    .shape({
      ...buildSchemaSelectCover('personOne'),
    })
    .required();
}

//----------------------------------------------------------------

//** Quote Summary -  Based on Select Cover validation */

export function schemaTwoPersonQuoteSummary() {
  return yup.object().shape({
    ...buildQuoteSummarySchema('personOne'),
    ...buildQuoteSummarySchema('personTwo'),
  });
}

export function schemaQuoteSummary() {
  return yup.object().shape({
    ...buildQuoteSummarySchema('personOne'),
  });
}
//----------------------------------------------------------------

//** Opening script - Contact Center */
const openingScriptValidation = (value) => {
  if (value === 'no') {
    return false;
  } else {
    return true;
  }
};

export function schemaOpeningScript() {
  return yup.object().shape({
    privacyRecordingQuestion: yup
      .string()
      .oneOf(['yes', 'no'])
      .required()
      .test('privacyRecordingQuestionValidation', '', openingScriptValidation),
    additionalSummaryQuestion: yup
      .string()
      .oneOf(['yes', 'no'])
      .required()
      .test('additionalSummaryQuestionValidation', '', openingScriptValidation),
    // optOutDirectMarketingQuestion: yup
    //   .string()
    //   .oneOf(["yes", "no"])
    //   .required()
    //   .test(
    //     "optOutDirectMarketingQuestionValidation",
    //     "",
    //     openingScriptValidation
    //   ),
  });
}
const aboutYouTCValidation = (value) => {
  if (value === 'no') {
    return false;
  } else {
    return true;
  }
};

export function aboutYouDisclosureSchema() {
  return yup.object().shape({
    aboutYouDisclosureTc: yup
      .string()
      // .oneOf(["yes", "no"])
      .required()
      .test('aboutYouDisclosureTcValid', '', aboutYouTCValidation),
  });
}

//** NEW */
const buildAboutYouInformationSchema = (person) => {
  return {
    [`${person}_title`]: yup.string().required(TITLE),
    [`${person}_firstName`]: yup
      .string()
      .trim()
      .required(FIRST_NAME_REQUIRED)
      .max(MAX_NUMBER_CHARACTERS, FIRST_NAME_LENGTH)
      .matches(FIRST_NAME_REGEX, {
        message: FIRST_NAME_NOT_VALID,
        excludeEmptyString: true, // skip regex on ""
      })
      // 2) Explicitly reject inputs that *originally* started with a space
      .test('no-leading-space', FIRST_NAME_NO_LEADING_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // still optional
        return !/^\s/.test(originalValue);
      })
      // 3) If you ALSO want to block "all spaces" (while still allowing empty after clear):
      .test('not-only-spaces', FIRST_NAME_NO_FULL_NAME_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // allow blank
        return originalValue.trim().length > 0; // reject "   "
      }),
    [`${person}_surname`]: yup
      .string()
      .trim() // trims before validation
      .required(SURNAME_REQUIRED)
      .max(MAX_NUMBER_CHARACTERS, SURNAME_LENGTH)
      .matches(FIRST_NAME_REGEX, {
        message: SURNAME_NOT_VALID,
        excludeEmptyString: true, // skip regex on ""
      })
      // 2) Explicitly reject inputs that *originally* started with a space
      .test('no-leading-space', SURNAME_NO_LEADING_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // still optional
        return !/^\s/.test(originalValue);
      })
      // 3) If you ALSO want to block "all spaces" (while still allowing empty after clear):
      .test('not-only-spaces', SURNAME_NO_FULL_NAME_SPACES, function () {
        const { originalValue } = this;
        if (originalValue == null || originalValue === '') return true; // allow blank
        return originalValue.trim().length > 0; // reject "   "
      }),
    [`${person}_state`]: yup
      .string()
      .oneOf(['act', 'nsw', 'vic', 'qld', 'nt', 'sa', 'tas', 'wa'])
      .required(STATE_REQUIRED),
  };
};

export function aboutYouInformationSchema() {
  return yup.object().shape({
    ...buildAboutYouInformationSchema('personOne'),
  });
}

//* Question options
const OPTION_SELECT = 'Please select one option';
const questionValidation = (value) => {
  if (value === 'no') {
    return false;
  } else {
    return true;
  }
};

// const otherInsuranceQuestionValidation = (value) => {
//   if (value === 'no') {
//     return true;
//   } else {
//     return false;
//   }
// };

const buildAboutYouDeclarationSchema = (person) => {
  return {
    [`${person}_residentQuestion`]: yup
      .string()
      .oneOf(['yes', 'no'])
      .required(OPTION_SELECT)
      .test('residentQuestion', '', questionValidation),
    aboutYouDisclosureTc: yup
      .string()
      .required()
      .test('aboutYouDisclosureTcValid', '', aboutYouTCValidation),
    [`${person}_otherAllianzInsuranceQuestion`]: yup
      .string()
      .oneOf(['yes', 'no'])
      .required(INSURANCE_QUESTION_REQUIRED),
    // .test('otherInsurance', '', otherInsuranceQuestionValidation),
  };
};

export function aboutYouDeclarationSchema() {
  return yup.object().shape({
    ...buildAboutYouDeclarationSchema('personOne'),
  });
}
